import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, ErrorMessage } from 'formik';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import BasicTextArea from '../../../../components/BasicTextArea';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import useApi from '../../../../hooks/useApi';
import {ProfileEditCandidateInterviewDataRequestFormValidationSchema} from './validate';

const ProfileEditCandidateInterviewDataRequestForm = props => {
  const {
    params: { initialValues },
    actions: { proceed, dismiss, handleError },
  } = props;

  const api = useApi();

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    const params = { ...values };
    Object.keys(params).forEach(k => {
      if (!params[k]) {
        delete params[k];
      }
    });

    const endpoint = params.id
      ? params.isMasterAccount
        ? ENDPOINT.FORM.SAVE.SU_EDIT_CANDIDATE_PROFILE_INTERVIEW_DATA
        : ENDPOINT.FORM.SAVE.EDIT_CANDIDATE_PROFILE_INTERVIEW_DATA
      : ENDPOINT.FORM.SAVE.PROFILE_CONFIDENTIAL_INTERVIEW_DATA;

    api.post(endpoint, params, {
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleSuccess: response => proceed(response),
    });
  };

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={{
            About: '',
            Characteristic: '',
            Compensation: '',
            ...initialValues,
          }}
          validationSchema={
            ProfileEditCandidateInterviewDataRequestFormValidationSchema
          }
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}

              <div
                className="form__group form__group--compact form__group--placeholders"
                style={{
                  maxWidth: '100%',
                  marginBottom: '25px',
                }}
              >
                <BasicLabel
                  name="About"
                  required={true}
                  text="Tell us about yourself and career goals"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicTextArea
                  rows={10}
                  name="About"
                  content={values.About || ''}
                  resize={false}
                  placeholder="Include information about your hometown, siblings, hobbies and interests alongside your three year to five year professional aspirations. The more details the better and you can always update this information."
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={(!errors.About && !!touched.About) || !touched.About}
                />

                <ErrorMessage
                  name="About"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact form__group--placeholders"
                style={{
                  maxWidth: '100%',
                  marginBottom: '25px',
                }}
              >
                <BasicLabel
                  name="Characteristic"
                  required={true}
                  text="What would you say is your greatest strength and greatest weakness?"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicTextArea
                  rows={10}
                  name="Characteristic"
                  content={values.Characteristic || ''}
                  resize={false}
                  placeholder="Age-old question. Short and sweet with some qualifying information is ideal."
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={
                    (!errors.Characteristic && !!touched.Characteristic) ||
                    !touched.Characteristic
                  }
                />

                <ErrorMessage
                  name="Characteristic"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact form__group--placeholders"
                style={{
                  maxWidth: '100%',
                  marginBottom: '25px',
                }}
              >
                <BasicLabel
                  name="Compensation"
                  required={true}
                  text="What are your compensation expectations for your next role?"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicTextArea
                  rows={10}
                  name="Compensation"
                  content={values.Compensation || ''}
                  resize={false}
                  placeholder="Feel free to be as specific as you like and use qualifying information if necessary."
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={
                    (!errors.Compensation && !!touched.Compensation) ||
                    !touched.Compensation
                  }
                />

                <ErrorMessage
                  name="Compensation"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup groupStyles={{ width: '100%' }}>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

export default ProfileEditCandidateInterviewDataRequestForm;
